import type { Localized } from '../types/notice'
import type { DashboardStats } from '../data/requestDashboard'
import { POTHOLE_ON_TIME } from '../data/requestDashboard'
import { flaggedFor, fmtDec, fmtNum, pctChange } from './requestDashboard'

export interface AtnaDataRow {
  label: Localized
  value: number
  /** Unit shown after the value, e.g. " %" or " j". */
  unit: string
  tone: 'ok' | 'risk' | 'late' | 'neutral'
}

export interface AtnaDataAnswer {
  text: Localized
  rows?: AtnaDataRow[]
  /** Max value used to scale the bars (defaults to 100). */
  max?: number
  source: Localized
}

export interface AtnaDataScope {
  /** e.g. "District 1", or null for the whole city. */
  label: Localized | null
  districtId: string | null
  period: Localized
  stats: DashboardStats
}

export const ATNA_DATA_SUGGESTIONS: Localized[] = [
  { fr: 'Quels dossiers nécessitent mon attention ?', en: 'Which files need my attention?' },
  { fr: 'Quels problèmes reviennent le plus ?', en: 'Which issues come up most?' },
  { fr: 'Où les délais se dégradent-ils ?', en: 'Where are turnaround times slipping?' },
  { fr: 'Quels résultats présenter aux citoyens ?', en: 'Which results should I share with citizens?' },
  { fr: 'Combien de demandes sont en cours à ce jour ?', en: 'How many requests are open today?' },
  { fr: 'Comment évolue la satisfaction des citoyens ?', en: 'How is citizen satisfaction trending?' },
]

const has = (q: string, words: string[]) => words.some((w) => q.includes(w))

/** Canned natural-language answers, built from the dashboard's demo figures. */
export function answerDataQuestion(question: string, scope: AtnaDataScope): AtnaDataAnswer {
  const q = question.toLowerCase()
  const s = scope.stats
  const where: Localized = scope.label
    ? { fr: ` dans le ${scope.label.fr}`, en: ` in ${scope.label.en}` }
    : { fr: ' à l’échelle de la ville', en: ' city-wide' }
  const source: Localized = {
    fr: `Requêtes · ${scope.label ? scope.label.fr : 'toute la ville'} · ${scope.period.fr.toLowerCase()}`,
    en: `Requests · ${scope.label ? scope.label.en : 'whole city'} · ${scope.period.en.toLowerCase()}`,
  }
  const flaggedList = flaggedFor(scope.districtId)
  const flagged = flaggedList.length > 0
  const cityWide = scope.districtId === null

  // How many requests are open today?
  if (has(q, ['en cours', 'ouvert', 'open'])) {
    const onTrack = s.open - s.openAtRisk - s.late
    return {
      text: {
        fr: `${s.open} demandes sont en cours${where.fr} au 30 sept. 2026 : ${onTrack} dans les délais, ${s.openAtRisk} proche${s.openAtRisk > 1 ? 's' : ''} de l’échéance et ${s.late} en retard (${s.latePrev} il y a 30 jours).`,
        en: `${s.open} requests are open${where.en} as of Sept. 30, 2026: ${onTrack} on time, ${s.openAtRisk} close to deadline and ${s.late} late (${s.latePrev} 30 days ago).`,
      },
      rows: [
        { label: { fr: 'Dans les délais', en: 'On time' }, value: onTrack, unit: '', tone: 'ok' },
        { label: { fr: 'Proches de l’échéance', en: 'Close to deadline' }, value: s.openAtRisk, unit: '', tone: 'risk' },
        { label: { fr: 'En retard', en: 'Late' }, value: s.late, unit: '', tone: 'late' },
      ],
      max: Math.max(1, s.open),
      source: { fr: 'Demandes ouvertes au 30 sept. 2026', en: 'Open requests as of Sept. 30, 2026' },
    }
  }

  // How is citizen satisfaction trending?
  if (has(q, ['évolue', 'evolue', 'satisfaction', 'trending'])) {
    const d = Math.round((s.satisfaction - s.satisfactionPrev) * 10) / 10
    const trend: Localized =
      d > 0
        ? { fr: 'en hausse', en: 'up' }
        : d < 0
          ? { fr: 'en légère baisse', en: 'slightly down' }
          : { fr: 'stable', en: 'stable' }
    return {
      text: {
        fr: `La satisfaction après traitement est ${trend.fr}${where.fr} : ${fmtDec(s.satisfaction, 'fr')} / 5, contre ${fmtDec(s.satisfactionPrev, 'fr')} la période précédente, sur ${fmtNum(s.surveyResponses, 'fr')} réponses au sondage.${s.surveyResponses < 50 ? ' Le nombre de réponses est faible : l’écart reste à confirmer.' : ''}`,
        en: `Satisfaction after handling is ${trend.en}${where.en}: ${fmtDec(s.satisfaction, 'en')} / 5, versus ${fmtDec(s.satisfactionPrev, 'en')} the previous period, from ${fmtNum(s.surveyResponses, 'en')} survey responses.${s.surveyResponses < 50 ? ' Few responses: the gap still needs confirming.' : ''}`,
      },
      rows: [
        { label: { fr: 'Période en cours (sur 100)', en: 'Current period (out of 100)' }, value: Math.round(s.satisfaction * 20), unit: '', tone: 'ok' },
        { label: { fr: 'Période précédente (sur 100)', en: 'Previous period (out of 100)' }, value: Math.round(s.satisfactionPrev * 20), unit: '', tone: 'neutral' },
      ],
      source,
    }
  }

  // Where are turnaround times slipping?
  if (has(q, ['dégrad', 'degrad', 'slipping', 'worse', 'where', 'où'])) {
    const geo: Localized = cityWide
      ? {
          fr: ` Sur le territoire, 2 districts passent sous le seuil : le district 3 (79 %, contre 86 %), où le secteur B compte 1 nid-de-poule en retard et 2 bacs proches de l’échéance, et le district 9 (77 %, contre 85 %), où les secteurs O et I comptent 1 lampadaire en retard et 3 demandes proches de l’échéance.`,
          en: ` Across the territory, 2 districts fall below the threshold: district 3 (79%, versus 86%), where sector B has 1 late pothole and 2 bins close to deadline, and district 9 (77%, versus 85%), where sectors O and I have 1 late streetlight and 3 requests close to deadline.`,
        }
      : flagged
        ? {
            fr: ` Dans votre territoire, ${flaggedList.length} demande${flaggedList.length > 1 ? 's sont' : ' est'} en retard ou proche${flaggedList.length > 1 ? 's' : ''} de l’échéance (secteur${new Set(flaggedList.map((r) => r.sector)).size > 1 ? 's' : ''} ${[...new Set(flaggedList.map((r) => r.sector))].join(', ')}).`,
            en: ` In your territory, ${flaggedList.length} request${flaggedList.length > 1 ? 's are' : ' is'} late or close to deadline (sector${new Set(flaggedList.map((r) => r.sector)).size > 1 ? 's' : ''} ${[...new Set(flaggedList.map((r) => r.sector))].join(', ')}).`,
          }
        : {
          fr: ` Aucune demande de vos secteurs n’est en retard ni proche de l’échéance ; le tableau par service de la carte donne le résultat de chaque secteur.`,
          en: ` No request in your sectors is late or close to its deadline; the map’s service table shows each sector’s result.`,
        }
    return {
      text: {
        fr: `Deux services sont sous le seuil de conformité de ${POTHOLE_ON_TIME.threshold} % à l’échelle de la ville : Nid-de-poule, à ${POTHOLE_ON_TIME.current} % dans les délais contre ${POTHOLE_ON_TIME.previous} % la période précédente, et Signalisation routière, à 76 %. Les autres services restent au-dessus.${geo.fr}`,
        en: `Two services are below the ${POTHOLE_ON_TIME.threshold}% compliance threshold city-wide: Pothole, at ${POTHOLE_ON_TIME.current}% on time versus ${POTHOLE_ON_TIME.previous}% the previous period, and Road signage, at 76%. The other services stay above.${geo.en}`,
      },
      rows: [
        { label: { fr: 'Nid-de-poule', en: 'Pothole' }, value: POTHOLE_ON_TIME.current, unit: ' %', tone: 'risk' },
        { label: { fr: 'Signalisation routière', en: 'Road signage' }, value: 76, unit: ' %', tone: 'risk' },
        { label: { fr: 'Éclairage de parc', en: 'Park lighting' }, value: 89, unit: ' %', tone: 'ok' },
        { label: { fr: 'Réparation lampadaire', en: 'Streetlight repair' }, value: 92, unit: ' %', tone: 'ok' },
        { label: { fr: 'Bac brisé', en: 'Broken bin' }, value: 96, unit: ' %', tone: 'ok' },
      ],
      source: { fr: 'Respect des SLA · toute la ville · période en cours', en: 'SLA compliance · whole city · current period' },
    }
  }

  // Which results should I share with citizens?
  if (has(q, ['résultat', 'resultat', 'présenter', 'presenter', 'valoris', 'result', 'share', 'satisf'])) {
    return {
      text: {
        fr: `${s.onTime} % des demandes ont été résolues dans les délais${where.fr} (${s.onTimePrev} % la période précédente), avec un délai médian de ${fmtDec(s.medianDays, 'fr')} jours (${fmtDec(s.medianDaysPrev, 'fr')} auparavant). Les citoyens donnent ${fmtDec(s.satisfaction, 'fr')} / 5 après traitement, sur ${fmtNum(s.surveyResponses, 'fr')} réponses au sondage (${s.surveyRate} % des demandes fermées).${s.late === 0 ? ' Aucune demande n’est en retard à ce jour.' : ''}`,
        en: `${s.onTime}% of requests were resolved on time${where.en} (${s.onTimePrev}% the previous period), with a median time of ${fmtDec(s.medianDays, 'en')} days (${fmtDec(s.medianDaysPrev, 'en')} before). Citizens rate ${fmtDec(s.satisfaction, 'en')} / 5 after handling, from ${fmtNum(s.surveyResponses, 'en')} survey responses (${s.surveyRate}% of closed requests).${s.late === 0 ? ' No request is late as of today.' : ''}`,
      },
      rows: [
        { label: { fr: 'Résolues dans les délais', en: 'Resolved on time' }, value: s.onTime, unit: ' %', tone: 'ok' },
        { label: { fr: 'Satisfaction (sur 100)', en: 'Satisfaction (out of 100)' }, value: Math.round(s.satisfaction * 20), unit: '', tone: 'ok' },
      ],
      source,
    }
  }

  // Which issues come up most?
  if (has(q, ['revienn', 'problème', 'probleme', 'fréquent', 'catégor', 'combien', 'volume', 'issue', 'most', 'how many'])) {
    const [first, second] = s.issues
    return {
      text: {
        fr: `${fmtNum(s.received, 'fr')} demandes reçues${where.fr} (${pctChange(s.received, s.receivedPrev, 'fr')} par rapport à la période précédente). « ${first.label.fr} » arrive en tête avec ${fmtNum(first.count, 'fr')} demandes (${pctChange(first.count, first.previous, 'fr')}), devant « ${second.label.fr} » (${fmtNum(second.count, 'fr')}).`,
        en: `${fmtNum(s.received, 'en')} requests received${where.en} (${pctChange(s.received, s.receivedPrev, 'en')} from the previous period). ${first.label.en} leads with ${fmtNum(first.count, 'en')} requests (${pctChange(first.count, first.previous, 'en')}), ahead of ${second.label.en.toLowerCase()} (${fmtNum(second.count, 'en')}).`,
      },
      rows: s.issues.map((i) => ({ label: i.label, value: i.count, unit: '', tone: 'neutral' as const })),
      max: first.count,
      source,
    }
  }

  // Which files need my attention?
  if (has(q, ['attention', 'dossier', 'nécessit', 'urgent', 'retard', 'late', 'file', 'need'])) {
    if (!flagged || s.late + s.openAtRisk === 0) {
      return {
        text: {
          fr: `Aucun dossier en retard ni proche de l’échéance${where.fr}. Les ${s.open} demandes en cours sont toutes dans les délais. Point à suivre : le service Nid-de-poule est à ${POTHOLE_ON_TIME.current} % à l’échelle de la ville, sous le seuil de ${POTHOLE_ON_TIME.threshold} %.`,
          en: `No file is late or close to its deadline${where.en}. All ${s.open} open requests are on time. Worth following: the Pothole service is at ${POTHOLE_ON_TIME.current}% city-wide, below the ${POTHOLE_ON_TIME.threshold}% threshold.`,
        },
        rows: [
          { label: { fr: 'En cours', en: 'Open' }, value: s.open, unit: '', tone: 'ok' },
          { label: { fr: 'Proches de l’échéance', en: 'Close to deadline' }, value: 0, unit: '', tone: 'risk' },
          { label: { fr: 'En retard', en: 'Late' }, value: 0, unit: '', tone: 'late' },
        ],
        max: Math.max(1, s.open),
        source,
      }
    }
    const lates = flaggedList.filter((r) => r.state === 'late')
    const lateText = (lang: 'fr' | 'en') =>
      lates
        .map((r) =>
          lang === 'fr'
            ? `${r.service.fr.toLowerCase()} du secteur ${r.sector} (${r.days} j pour un SLA de ${r.sla} ; ${r.reason ? `motif au dossier : ${r.reason.fr}` : 'aucune explication au dossier'})`
            : `${r.service.en.toLowerCase()} in sector ${r.sector} (${r.days} d against a ${r.sla}-day SLA; ${r.reason ? `reason in the file: ${r.reason.en}` : 'no explanation in the file'})`,
        )
        .join(lang === 'fr' ? ' et ' : ' and ')
    const risks = flaggedList.length - lates.length
    return {
      text: {
        fr: `${flaggedList.length} dossiers sur ${s.open} en cours demandent votre attention${where.fr}. En retard : ${lateText('fr') || 'aucun'}. Proches de l’échéance : ${risks} demandes, toutes à 12 jours sur 14.${cityWide ? ' Les districts 3 et 9 en regroupent l’essentiel.' : ''}`,
        en: `${flaggedList.length} of ${s.open} open files need your attention${where.en}. Late: ${lateText('en') || 'none'}. Close to deadline: ${risks} requests, all at 12 of 14 days.${cityWide ? ' Districts 3 and 9 hold most of them.' : ''}`,
      },
      rows: flaggedList.map((r) => ({
        label: { fr: `${r.service.fr} · secteur ${r.sector}`, en: `${r.service.en} · sector ${r.sector}` },
        value: r.days,
        unit: ` / ${r.sla} j`,
        tone: r.state,
      })),
      max: 18,
      source: { fr: `Demandes ouvertes au 30 sept. 2026`, en: `Open requests as of Sept. 30, 2026` },
    }
  }

  return {
    text: {
      fr: 'Je n’ai pas trouvé de donnée correspondante. Je peux vous répondre sur les dossiers à suivre, les problèmes les plus fréquents, l’évolution des délais ou les résultats à présenter aux citoyens.',
      en: 'I couldn’t find matching data. I can answer about files to follow, the most frequent issues, turnaround trends or results to share with citizens.',
    },
    source,
  }
}
